import type { Story } from "@theirstory/common/api_types/backend";

import { CALL_PROCESSING_STATUS } from "../../../store/reducers/dailyCallReducer";

export const selectSentenceHandler = (
  startTime: number,
  endTime: number,
  classes: Record<"highlighted" | "unHighlighted", string>,
  isChaptersView: boolean,
  skipHighlight = false,
  // Opt-in (defaults keep the Hub's timed highlight): `persist` keeps the words
  // highlighted instead of fading them after 5s (the chat panel's citation
  // highlight); `scroll: false` re-applies without moving the transcript.
  { persist = false, scroll = true }: { persist?: boolean; scroll?: boolean } = {}
): Promise<void> =>
  new Promise((resolve, reject) => {
    // Validate parameters: check for undefined, NaN, and invalid ranges
    if (
      !classes ||
      startTime === undefined ||
      endTime === undefined ||
      isNaN(startTime) ||
      isNaN(endTime) ||
      startTime >= endTime
    ) {
      reject(new Error("Invalid parameters"));
      return;
    }

    const highlightedClass = classes.highlighted;
    const unHighlightedClass = classes.unHighlighted;
    const paragraphElement = document.getElementById(isChaptersView ? "bookmarksBox" : "paragraph");
    const words = paragraphElement?.getElementsByTagName("span");

    if (words) {
      let scrolled = false;
      const wordsToHighlight: HTMLElement[] = [];

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const wordTime = parseFloat(word.id);

        if (wordTime >= startTime && wordTime <= endTime) {
          if (!scrolled && scroll) {
            word.scrollIntoView({ behavior: "smooth", block: "center" });
            scrolled = true;
          }
          wordsToHighlight.push(word);
        }
      }

      // Skip highlighting if skipHighlight is true (e.g., when type=keyword)
      if (skipHighlight) {
        resolve();
        return;
      }

      // We add the class to the words to highlight
      wordsToHighlight.forEach((word) => {
        word.classList.add(highlightedClass);
      });

      if (persist) {
        resolve();
        return;
      }

      requestAnimationFrame(() => {
        setTimeout(() => {
          wordsToHighlight.forEach((word) => {
            word.classList.replace(highlightedClass, unHighlightedClass);
          });
          resolve();
        }, 5000);
      });
    } else {
      reject(new Error("No words found to highlight"));
    }
  });

export const isStoryPlaybackReady = (storyData: Story) =>
  // legacy stories that don't have call_processing_status are considered playable to avoid blocking features for them
  !storyData?.call_processing_status ||
  // MP4 playback is ready, even if transcription is still running
  storyData?.call_processing_status === CALL_PROCESSING_STATUS.PLAYBACK_READY ||
  // for new stories, we check the call_processing_status field to determine if the encoding is completed
  storyData?.call_processing_status === CALL_PROCESSING_STATUS.COMPLETED ||
  // uploaded stories already have a source file available for playback
  storyData?.is_upload ||
  // if there is a completed transcribe operation, we can be sure the video encoding is completed, as transcription is the step after encoding in the processing flow
  storyData?.asyncOperations?.some((op) => op.type === "transcribe" && op.status === "completed") ||
  // Testimonials don't follow the same processing flow as Daily calls, so we consider them playable right away for now
  storyData?.prepsession?.type === "solo";
